/**
 * Notification Service
 * Handles creating and managing notifications through the backend API
 * Supports rich notifications with metadata, cross-device persistence, and real-time updates
 */

import { toast } from 'sonner';
import {fetchNotificationSnapshot,type NotificationSnapshot} from './notificationSnapshot';
import { API_BASE, getAuthHeaders } from '../utils/supabase/client';
import { Notification } from '../types/notifications';
import { buildNotificationTemplate, NotificationTemplateId } from '../notifications/templates';
import { decorateNotificationWithPolicy, shouldPersistNotificationType, shouldPersistTemplateNotification } from '../notifications/policy';
import { NativeBridge } from './NativeBridge';

export interface ToastNotification {
  id: string;
  type: 'success' | 'error' | 'info' | 'warning';
  title?: string;
  message: string;
  userId?: string;
  userName?: string;
  metadata?: any;
  timestamp: string;
  read: boolean;
}

interface ToastOptions {
  title?: string;
  userId?: string;
  userName?: string;
  metadata?: any;
  persist?: boolean;
}

class NotificationServiceClass {
  private apiUrl = API_BASE;

  /**
   * Create a rich notification
   */
  async createNotification(notification: Omit<Notification, 'id' | 'createdAt'> & { createdAt?: string }): Promise<Notification | null> {
    try {
      if (!shouldPersistNotificationType(notification.type)) {
        return null;
      }

      const newNotification: Notification = {
        ...decorateNotificationWithPolicy(notification),
        id: crypto.randomUUID(),
        // Prefer caller-supplied timestamp (e.g. email internalDate) so the
        // notification reflects the real event time, not the poll time.
        createdAt: notification.createdAt ?? new Date().toISOString(),
        read: false,
      };

      const response = await fetch(`${this.apiUrl}/notifications`, {
        method: 'POST',
        headers: await getAuthHeaders({ 'Content-Type': 'application/json' }),
        body: JSON.stringify(newNotification),
      });

      if (!response.ok) {
        console.error('Failed to create notification:', await response.text());
        return null;
      }

      const data = await response.json();
      return data.notification;
    } catch (error) {
      console.error('Error creating notification:', error);
      return null;
    }
  }

  /**
   * Create multiple notifications at once (for notifying multiple users)
   */
  async createBatchNotifications(notifications: Omit<Notification, 'id' | 'createdAt'>[]): Promise<boolean> {
    try {
      const persistedNotifications = notifications.filter(notif => shouldPersistNotificationType(notif.type));
      if (persistedNotifications.length === 0) {
        return true;
      }

      const newNotifications: Notification[] = persistedNotifications.map(notif => ({
        ...decorateNotificationWithPolicy(notif),
        id: crypto.randomUUID(),
        createdAt: new Date().toISOString(),
        read: false,
      }));

      const response = await fetch(`${this.apiUrl}/notifications/batch`, {
        method: 'POST',
        headers: await getAuthHeaders({ 'Content-Type': 'application/json' }),
        body: JSON.stringify({ notifications: newNotifications }),
      });

      if (!response.ok) {
        console.error('Failed to create batch notifications:', await response.text());
        return false;
      }

      return true;
    } catch (error) {
      console.error('Error creating batch notifications:', error);
      return false;
    }
  }

  /**
   * Show success toast and save to backend
   */
  success(message: string, options?: ToastOptions): void {
    toast.success(options?.title || message, {
      description: options?.title ? message : undefined,
    });

    // Only save to backend if userId is provided
    if (options?.persist && options.userId) {
      this.saveToBackend({
        id: crypto.randomUUID(),
        type: 'success',
        title: options?.title || message,
        message,
        userId: options.userId,
        userName: options?.userName,
        metadata: options?.metadata,
        timestamp: new Date().toISOString(),
        read: false,
      });
    }
  }

  /**
   * Clear all notifications for a user
   */
  async clearAll(userId: string): Promise<boolean> {
    try {
      if (!userId) {
        console.error('Failed to clear notifications: no userId provided');
        return false;
      }

      const response = await fetch(`${this.apiUrl}/notifications?userId=${encodeURIComponent(userId)}`, {
        method: 'DELETE',
        headers: await getAuthHeaders({ 'Content-Type': 'application/json' }),
      });

      if (!response.ok) {
        const text = await response.text();
        console.error('Failed to clear notifications:', text);
        return false;
      }

      return true;
    } catch (error) {
      console.error('Failed to clear notifications', error);
      return false;
    }
  }

  /**
   * Show error toast and save to backend
   */
  error(message: string, options?: ToastOptions): void {
    toast.error(options?.title || message, {
      description: options?.title ? message : undefined,
    });

    // Only save to backend if userId is provided
    if (options?.persist && options.userId) {
      this.saveToBackend({
        id: crypto.randomUUID(),
        type: 'error',
        title: options?.title || message,
        message,
        userId: options.userId,
        userName: options?.userName,
        metadata: options?.metadata,
        timestamp: new Date().toISOString(),
        read: false,
      });
    }
  }

  /**
   * Show info toast and save to backend
   */
  info(message: string, options?: ToastOptions): void {
    toast.info(options?.title || message, {
      description: options?.title ? message : undefined,
    });

    // Only save to backend if userId is provided
    if (options?.persist && options.userId) {
      this.saveToBackend({
        id: crypto.randomUUID(),
        type: 'info',
        title: options?.title || message,
        message,
        userId: options.userId,
        userName: options?.userName,
        metadata: options?.metadata,
        timestamp: new Date().toISOString(),
        read: false,
      });
    }
  }

  /**
   * Show warning toast and save to backend
   */
  warning(message: string, options?: ToastOptions): void {
    toast.warning(options?.title || message, {
      description: options?.title ? message : undefined,
    });

    // Only save to backend if userId is provided
    if (options?.persist && options.userId) {
      this.saveToBackend({
        id: crypto.randomUUID(),
        type: 'warning',
        title: options?.title || message,
        message,
        userId: options.userId,
        userName: options?.userName,
        metadata: options?.metadata,
        timestamp: new Date().toISOString(),
        read: false,
      });
    }
  }

  /**
   * Get all notifications from backend
   */
  async getNotifications(userId?: string): Promise<ToastNotification[]> {
    try {
      return (await this.getNotificationSnapshot<ToastNotification>(userId)).notifications;
    } catch (error) {
      // Silently handle - typically network/auth issues
      console.warn('Unable to fetch notifications:', error instanceof Error ? error.message : 'Unknown error');
      return [];
    }
  }

  /** List and authoritative count stay together through all reconciliation pages. */
  async getNotificationSnapshot<T=Notification>(userId?:string,isCurrent:()=>boolean=()=>true,filters?:{types?:readonly string[];companyId?:string;unreadOnly?:boolean}):Promise<NotificationSnapshot<T>> {
    if(filters?.types?.length===0)return {notifications:[],unreadCount:0};
    const url=new URL(`${this.apiUrl}/notifications`);
    if(userId)url.searchParams.set('userId',userId);
    if(filters?.types)url.searchParams.set('types',filters.types.join(','));
    if(filters?.companyId)url.searchParams.set('companyId',filters.companyId);
    if(filters?.unreadOnly)url.searchParams.set('unreadOnly','true');
    return fetchNotificationSnapshot<T>(url.toString(),async next=>{
      const headers=await getAuthHeaders();
      if(!isCurrent())throw new Error('Notification scope changed.');
      return fetch(next,{headers,signal:AbortSignal.timeout(10000)});
    },isCurrent);
  }

  /**
   * Mark notification as read
   */
  async markAsRead(notificationId: string): Promise<void> {
    const url = `${this.apiUrl}/notifications/${notificationId}/read`;
    
    try {
      const response = await fetch(url, {
        method: 'PATCH',
        headers: await getAuthHeaders(),
      });
      
      if (!response.ok) {
        const errorText = await response.text();
        console.error('❌ [NotificationService] Failed to mark notification as read:', errorText);
        throw new Error(`Failed to mark notification as read: ${response.status} - ${errorText}`);
      }
      
      await response.json();
    } catch (error) {
      console.error('❌ [NotificationService] Error marking notification as read:', error);
      throw error;
    }
  }

  /**
   * Mark all notifications as read for a user
   */
  async markAllAsRead(userId: string): Promise<void> {
    try {
      const response = await fetch(`${this.apiUrl}/notifications/mark-all-read`, {
        method: 'PATCH',
        headers: await getAuthHeaders({ 'Content-Type': 'application/json' }),
        body: JSON.stringify({ userId }),
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.error('Failed to mark all notifications as read:', response.status, errorText);
        throw new Error(`Failed to mark all as read: ${response.status}`);
      }

      await response.json();
    } catch (error) {
      console.error('Error marking all notifications as read:', error);
      throw error;
    }
  }

  /**
   * Shorten a notification's message via the server (Claude on web — native
   * iOS/macOS do this on-device with Apple Intelligence instead). Idempotent:
   * the server returns the cached `short_message` if one already exists
   * rather than calling Claude again.
   */
  async shortenNotification(notificationId: string): Promise<Notification> {
    const response = await fetch(`${this.apiUrl}/notifications/${notificationId}/shorten`, {
      method: 'POST',
      headers: await getAuthHeaders(),
    });

    if (!response.ok) {
      const errorText = await response.text();
      console.error('Failed to shorten notification:', response.status, errorText);
      throw new Error(`Failed to shorten notification: ${response.status}`);
    }

    const data = await response.json();
    return data.notification;
  }

  /**
   * Delete notification
   */
  async deleteNotification(notificationId: string): Promise<void> {
    try {
      const response = await fetch(`${this.apiUrl}/notifications/${notificationId}`, {
        method: 'DELETE',
        headers: await getAuthHeaders(),
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.error('Failed to delete notification:', response.status, errorText);
        throw new Error(`Failed to delete notification: ${response.status}`);
      }

      await response.json();
    } catch (error) {
      console.error('Error deleting notification:', error);
      throw error;
    }
  }

  /**
   * Sync the app icon badge for a user.
   * Call on app open to fix any stale badge counts from previously-read notifications.
   */
  async syncBadge(userId: string): Promise<void> {
    try {
      await fetch(`${this.apiUrl}/notifications/sync-badge`, {
        method: 'POST',
        headers: await getAuthHeaders({ 'Content-Type': 'application/json' }),
        body: JSON.stringify({ userId }),
      });
    } catch {
      // Non-critical — badge sync failure should never affect the UI
    }
  }

  /**
   * Show toast notification
   */
  show(notification: ToastNotification): void {
    if (NativeBridge.getPlatform() === 'ios' && NativeBridge.isRunningNatively()) {
      const userInfo = notification.metadata && typeof notification.metadata === 'object'
        ? { ...notification.metadata }
        : {};
      NativeBridge.presentNotification({
        title: notification.title,
        body: notification.message,
        userInfo,
      });
    } else {
    toast.info(notification.message, {
      description: notification.title ? notification.message : undefined,
    });
    }

    // Only save to backend if userId is provided
    if (notification.userId) {
      this.saveToBackend({
        id: crypto.randomUUID(),
        type: 'info',
        title: notification.title,
        message: notification.message,
        userId: notification.userId,
        userName: notification.userName,
        metadata: notification.metadata,
        timestamp: new Date().toISOString(),
        read: false,
      });
    }
  }

  /**
   * Save notification to backend
   */
  private async saveToBackend(notification: ToastNotification): Promise<void> {
    try {
      const response = await fetch(`${this.apiUrl}/notifications`, {
        method: 'POST',
        headers: await getAuthHeaders({ 'Content-Type': 'application/json' }),
        body: JSON.stringify(notification),
      });

      if (!response.ok) {
        console.error('Failed to save notification to backend:', await response.text());
      }
    } catch (error) {
      // Silently fail - don't break the UI if backend is down
      console.error('Error saving notification:', error);
    }
  }

  /**
   * Template-based notification helper
   */
  template(id: NotificationTemplateId, ctx: Record<string, any> = {}, options?: ToastOptions) {
    const tpl = buildNotificationTemplate(id, ctx);
    const payload = {
      title: tpl.title,
      userId: options?.userId,
      userName: options?.userName,
      metadata: { ...options?.metadata, templateId: id, ctx },
      persist: options?.persist ?? shouldPersistTemplateNotification(id),
    };
    switch (tpl.type) {
      case 'success':
        this.success(tpl.message, payload);
        break;
      case 'error':
        this.error(tpl.message, payload);
        break;
      case 'warning':
        this.warning(tpl.message, payload);
        break;
      default:
        this.info(tpl.message, payload);
    }
  }
}

export const NotificationService = new NotificationServiceClass();
